import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useState } from "react";
import { Menu, X, Store, ShoppingBag } from "lucide-react";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import CurrencyPicker from "@/components/CurrencyPicker";
import { useI18n } from "@/context/I18nContext";

export default function PublicNav() {
    const { user } = useAuth();
    const { t } = useI18n();
    const [open, setOpen] = useState(false);
    const location = useLocation();

    const links = [
        { to: "/#how-it-works", label: t("nav.how_it_works") },
        { to: "/#rates", label: t("nav.rates") },
        { to: "/#faq", label: t("nav.faq") },
        { to: "/contact", label: t("nav.contact") },
    ];

    const isMarketplace = location.pathname === "/marketplace";
    const isBuyPage = location.pathname === "/buy-giftcard";

    return (
        <header className={`sticky top-0 z-40 backdrop-blur-md border-b ${isMarketplace ? "bg-[#0f0a1e]/80 border-white/10" : "bg-white/80 dark:bg-slate-900/85 border-slate-100 dark:border-slate-800"}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                <Link to="/" data-testid="logo-home" className="flex items-center mr-4 shrink-0">
                    <Logo className="h-9" variant={isMarketplace ? "dark" : "light"} testId="public-nav-logo" />
                </Link>

                <nav className="hidden md:flex items-center gap-6">
                    {links.map((l) => (
                        <a key={l.to} href={l.to}
                            className={`text-sm font-medium whitespace-nowrap transition-colors ${
                                isMarketplace
                                    ? "text-violet-200/70 hover:text-white"
                                    : location.pathname === l.to
                                        ? "text-slate-900 dark:text-white"
                                        : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                            }`}
                            data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                            {l.label}
                        </a>
                    ))}
                    {/* Buy Gift Cards link */}
                    <Link to="/buy-giftcard"
                        className={`text-sm font-semibold inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border transition-all whitespace-nowrap ${
                            isBuyPage
                                ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
                                : "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                        }`}>
                        <ShoppingBag size={13} /> Buy Gift Cards
                    </Link>
                    {/* Marketplace link */}
                    <Link to="/marketplace"
                        className={`text-sm font-semibold inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border transition-all whitespace-nowrap ${
                            isMarketplace
                                ? "bg-violet-500/20 border-violet-500/40 text-violet-300"
                                : "bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-100"
                        }`}>
                        <Store size={13} /> Marketplace
                    </Link>
                </nav>

                <div className="hidden md:flex items-center gap-2 shrink-0">
                    <CurrencyPicker />
                    <LanguageSwitcher />
                    <ThemeToggle />
                    {user && user !== false ? (
                        <Link to={user.role === "admin" ? "/admin" : "/dashboard"}
                            data-testid="nav-dashboard-link"
                            className="bg-brand hover:bg-brand-500 text-white text-sm font-semibold px-5 py-2.5 rounded-full shadow-button transition-all hover:-translate-y-0.5">
                            {t("common.dashboard")}
                        </Link>
                    ) : (
                        <>
                            <Link to="/login" data-testid="nav-login-button"
                                className={`text-sm font-semibold px-5 py-2.5 rounded-full border transition-all ${
                                    isMarketplace
                                        ? "text-violet-200 border-white/20 hover:bg-white/10"
                                        : "text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-600 hover:border-slate-300"
                                }`}>
                                {t("common.login")}
                            </Link>
                            <Link to="/register" data-testid="nav-signup-button"
                                className={`text-sm font-semibold px-5 py-2.5 rounded-full shadow-button transition-all hover:-translate-y-0.5 ${
                                    isMarketplace
                                        ? "bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white"
                                        : "bg-brand hover:bg-brand-500 text-white"
                                }`}>
                                {t("common.signup")}
                            </Link>
                        </>
                    )}
                </div>

                <div className="md:hidden flex items-center gap-1">
                    <CurrencyPicker compact />
                    <LanguageSwitcher compact />
                    <ThemeToggle />
                    <button className="p-2 rounded-lg hover:bg-slate-100" onClick={() => setOpen(o => !o)}
                        data-testid="mobile-menu-toggle" aria-label="Toggle menu">
                        {open ? <X size={20} className={isMarketplace ? "text-white" : ""} /> : <Menu size={20} className={isMarketplace ? "text-white" : ""} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className={`md:hidden border-t px-4 py-4 space-y-3 ${isMarketplace ? "bg-[#1a0533] border-white/10" : "bg-white border-slate-100"}`}>
                    {links.map((l) => (
                        <a key={l.to} href={l.to} className={`block text-sm font-medium ${isMarketplace ? "text-violet-200/70" : "text-slate-700"}`}>
                            {l.label}
                        </a>
                    ))}
                    <Link to="/buy-giftcard" className="flex items-center gap-1.5 text-sm font-semibold text-emerald-600">
                        <ShoppingBag size={13} /> Buy Gift Cards
                    </Link>
                    <Link to="/marketplace" className="flex items-center gap-1.5 text-sm font-semibold text-violet-600">
                        <Store size={13} /> Marketplace
                    </Link>
                    <div className="flex gap-2 pt-2">
                        {user && user !== false ? (
                            <Link to={user.role === "admin" ? "/admin" : "/dashboard"}
                                className="flex-1 bg-brand text-white text-center text-sm font-semibold px-4 py-2.5 rounded-full">
                                Dashboard
                            </Link>
                        ) : (
                            <>
                                <Link to="/login" className={`flex-1 text-center text-sm font-semibold px-4 py-2.5 rounded-full border ${isMarketplace ? "text-violet-200 border-white/20" : "text-slate-700 border-slate-200"}`}>
                                    Login
                                </Link>
                                <Link to="/register" className={`flex-1 text-center text-sm font-semibold px-4 py-2.5 rounded-full ${isMarketplace ? "bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white" : "bg-brand text-white"}`}>
                                    Sign Up
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}

